import { useEffect, useState, useCallback, useRef } from "react";
import client from "../api/client";
import { subscribe, publish } from "../utils/swUpdateBus";

const STORAGE_KEY = "app_build_version";
const HEARTBEAT_INTERVAL_MS = 20 * 1000;
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const STARTUP_RETRY_DELAYS_MS = [3000, 10000, 30000];
const CLEAR_TIMEOUT_MS = 3000;
const CONTROLLER_CHANGE_TIMEOUT_MS = 3000;

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

// The interval/timeout registration is a module-level singleton so mounting
// this hook from both Sidebar (always mounted) and the mobile banner
// (conditionally mounted) never results in duplicate polling or a double
// "update available" notification.
let pollerStarted = false;
let lastCheckedAt = 0;

// Test-only escape hatch to reset the module-level singleton between tests -
// production code never calls this.
export function __resetForTests() {
  pollerStarted = false;
  lastCheckedAt = 0;
}

async function fetchLatestVersion() {
  const res = await client.get("/app-version");
  return res.data?.version;
}

async function checkVersion() {
  try {
    const version = await fetchLatestVersion();
    if (!version) return false;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      localStorage.setItem(STORAGE_KEY, version);
      publish({ type: "version-known", version });
      return true;
    }

    if (stored !== version) {
      publish({ type: "version", version });
    }
    return true;
  } catch (error) {
    return false;
  }
}

function startPoller() {
  if (pollerStarted) return;
  pollerStarted = true;

  const runFirstCheckWithRetries = async () => {
    const succeeded = await checkVersion();
    lastCheckedAt = Date.now();
    if (succeeded) return;

    for (const delay of STARTUP_RETRY_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      const ok = await checkVersion();
      lastCheckedAt = Date.now();
      if (ok) break;
    }
  };

  runFirstCheckWithRetries();

  setInterval(() => {
    if (Date.now() - lastCheckedAt >= POLL_INTERVAL_MS) {
      checkVersion();
      lastCheckedAt = Date.now();
    }
  }, HEARTBEAT_INTERVAL_MS);

  const onVisible = () => {
    if (document.visibilityState === "visible") {
      checkVersion();
      lastCheckedAt = Date.now();
    }
  };
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("pageshow", onVisible);
}

async function clearCachesAndServiceWorkers() {
  if ("caches" in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
}

export default function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateType, setUpdateType] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);
  const [currentVersion, setCurrentVersion] = useState(() => localStorage.getItem(STORAGE_KEY));

  useEffect(() => {
    startPoller();

    const unsubscribe = subscribe((event) => {
      if (event.type === "version-known") {
        setCurrentVersion(event.version);
      } else if (event.type === "version") {
        setUpdateAvailable(true);
        setUpdateType("version");
        setPendingVersion(event.version);
      } else if (event.type === "sw") {
        setUpdateAvailable(true);
        setUpdateType("sw");
        setPendingRegistration(event.registration);
      }
    });

    return unsubscribe;
  }, []);

  const openConfirm = useCallback(() => setIsConfirmOpen(true), []);
  const closeConfirm = useCallback(() => setIsConfirmOpen(false), []);

  const confirmAndReload = useCallback(async () => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    setIsUpdating(true);

    const clearAndReload = async () => {
      try {
        await withTimeout(clearCachesAndServiceWorkers(), CLEAR_TIMEOUT_MS);
      } catch (error) {
        console.error("Clearing caches before update failed:", error);
      }
      window.location.reload();
    };

    if (pendingVersion) {
      localStorage.setItem(STORAGE_KEY, pendingVersion);
    }

    if (updateType === "sw" && pendingRegistration?.waiting) {
      let hasReloaded = false;
      const reloadOnce = (reload) => {
        if (hasReloaded) return;
        hasReloaded = true;
        reload();
      };
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => reloadOnce(() => window.location.reload()),
        { once: true }
      );
      pendingRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
      setTimeout(() => reloadOnce(clearAndReload), CONTROLLER_CHANGE_TIMEOUT_MS);
      return;
    }

    await clearAndReload();
  }, [updateType, pendingRegistration, pendingVersion]);

  return { updateAvailable, updateType, isConfirmOpen, isUpdating, openConfirm, closeConfirm, confirmAndReload, currentVersion };
}
